import { type GraphQLResult } from 'aws-amplify/api';

// LantanaAPIの型を再エクスポート + 拡張
export * from '@/LantanaAPI';

// @aws-amplify/api-graphql で内部定義されている型
export type GeneratedQuery<InputType, OutputType> = string & {
    __generatedQueryInput: InputType;
    __generatedQueryOutput: OutputType;
};
export type GeneratedMutation<InputType, OutputType> = string & {
    __generatedMutationInput: InputType;
    __generatedMutationOutput: OutputType;
};
export type GeneratedSubscription<InputType, OutputType> = string & {
    __generatedSubscriptionInput: InputType;
    __generatedSubscriptionOutput: OutputType;
};
export interface Subscription {
    unsubscribe(): void;
    readonly closed: boolean;
}
export interface GraphqlSubscriptionMessage<T> {
    data: T;
}

/**
 * GraphQL エラーレスポンス用の統一的エラークラス
 * 
 * GraphQL操作でエラーが返された場合に使用する専用Errorクラス。
 * リクエスト追跡とエラー詳細の構造化された保持が可能。
 * 
 * @example
 * ```typescript
 * // エラー検出と自動throw
 * GraphQLResponseError.throwIfErrors(result, {
 *   requestId: 'req-123',
 *   operation: 'executeQuery'
 * });
 * 
 * // 手動でエラー作成
 * throw new GraphQLResponseError(
 *   'Operation failed',
 *   [{ message: 'Invalid input' }],
 *   { requestId: 'req-123' }
 * );
 * ```
 */
export class GraphQLResponseError extends Error {
    /** GraphQLエラー配列（元エラー情報の構造化保存） */
    public readonly graphqlErrors: Array<{
        message: string;
        extensions?: { code?: string; };
        path?: readonly (string | number)[];
    }>;
    /** リクエスト追跡用ID（ログ相関に使用） */
    public readonly requestId?: string;
    /** 実行された操作名（デバッグ・監視用） */
    public readonly operation?: string;

    /**
     * GraphQLResponseError コンストラクタ
     * 
     * @param message エラーメッセージ
     * @param graphqlErrors GraphQLエラー配列
     * @param options オプション設定
     * @param options.requestId リクエスト追跡ID
     * @param options.operation 操作名
     */
    constructor(
        message: string,
        graphqlErrors: Array<{
            message: string;
            extensions?: { code?: string; };
            path?: readonly (string | number)[];
        }>,
        options?: {
            requestId?: string;
            operation?: string;
        }
    ) {
        super(message);
        this.name = 'GraphQLResponseError';
        this.graphqlErrors = graphqlErrors;
        this.requestId = options?.requestId;
        this.operation = options?.operation;

        // Error.captureStackTrace が利用可能な場合のみ使用
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, GraphQLResponseError);
        }
    }

    /**
     * GraphQLResult からエラーを検出してthrowする
     * 
     * GraphQL操作結果にエラーが含まれている場合、
     * 自動的にGraphQLResponseErrorを投げる。
     * エラーがない場合は何もしない。
     * 
     * @param result GraphQL操作結果
     * @param options オプション設定
     * @param options.requestId リクエスト追跡ID
     * @param options.operation 操作名
     * @throws {GraphQLResponseError} エラーが検出された場合
     * 
     * @example
     * ```typescript
     * const result = await client.graphql({ query });
     * GraphQLResponseError.throwIfErrors(result, {
     *   requestId: 'req-123',
     *   operation: 'executeQuery'
     * });
     * // エラーなしの場合のみここに到達
     * return result.data;
     * ```
     */
    static throwIfErrors<T>(
        result: GraphQLResult<T>,
        options?: {
            requestId?: string;
            operation?: string;
        }
    ): void {
        if (result.errors && result.errors.length > 0) {
            const errorMessage = result.errors
                .map((error: any) => error.message)
                .join('; ');

            throw new GraphQLResponseError(
                `GraphQL operation failed: ${errorMessage}`,
                result.errors.map((error: any) => ({
                    message: error.message,
                    extensions: error.extensions,
                    path: error.path
                })),
                options
            );
        }
    }
}
